import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import type {
	TPolicyHead,
	TPolicyVersionBase,
	TPolicyVersionSummary,
} from "../shared/policies"

export type TCancellationPolicyCondition = {
	id: number
	min_days_before_checkin: number
	max_days_before_checkin: number
	cancellation_percentage: string
}

export type TCancellationPolicyRule = {
	id: number
	valid_from: string | null
	valid_to: string | null
	conditions: TCancellationPolicyCondition[]
}

export type TCancellationPolicy = TPolicyVersionBase & {
	rules: TCancellationPolicyRule[]
}

export type TCancellationPolicyVersion = TPolicyVersionSummary

export type TCancellationPolicyDetail = TPolicyHead<TCancellationPolicy>

// Payload Type
export type TCancellationPolicyPayload = {
	name: string
	additional_info?: string
	rules: {
		valid_from: string | null
		valid_to: string | null
		conditions: {
			min_days_before_checkin: number
			max_days_before_checkin: number
			cancellation_percentage: string
		}[]
	}[]
}

export function cancellationPoliciesXHR(xhr: XHRInstance) {
	return {
		async get(params?: Record<string, unknown>) {
			return await xhr
				.get<
					IListResponse<TCancellationPolicyDetail>
				>("/cancellation-policy-heads", { params })
				.then((resp) => resp.data)
		},

		async create(
			payload: Omit<TCancellationPolicyPayload, "name"> & { name?: string }
		): Promise<TCancellationPolicyDetail> {
			return xhr
				.post<{
					data: TCancellationPolicyDetail
				}>("/cancellation-policy-heads", payload)
				.then((resp) => resp.data.data)
		},

		async update(
			detailId: number,
			payload: TCancellationPolicyPayload
		): Promise<TCancellationPolicyDetail | { message: string }> {
			return xhr
				.put<{
					data: TCancellationPolicyDetail | { message: string }
				}>(`/cancellation-policy-heads/${detailId}`, payload)
				.then((resp) => resp.data.data)
		},

		async disable(detailId: number): Promise<void> {
			await xhr.delete(`/cancellation-policy-heads/${detailId}`)
		},

		async restore(detailId: number): Promise<void> {
			await xhr.patch(`/cancellation-policy-heads/${detailId}/restore`)
		},

		async getVersions(id: number, params?: Record<string, unknown>) {
			return await xhr
				.get<
					IListResponse<TCancellationPolicyVersion>
				>(`/cancellation-policy-heads/${id}/cancellation-policies`, { params })
				.then((resp) => resp.data)
		},

		async getVersionById(id: number): Promise<TCancellationPolicy> {
			return xhr
				.get<{ data: TCancellationPolicy }>(`/cancellation-policies/${id}`)
				.then((resp) => resp.data.data)
		},
	}
}
